import { css, html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

const MAX_LEN = 150;

function codepointLength(s: string): number {
  let n = 0;
  for (const _ of s) n++;
  return n;
}

export class BizResponseEntry extends LitElement {
  static properties = {
    prompt: { type: String },
    slots: { attribute: false },
    freeSlots: { type: Number, attribute: 'free-slots' },
    _drafts: { state: true },
    _sentSlots: { state: true },
  };

  declare prompt: string;
  declare slots: number[];
  declare freeSlots: number;
  declare _drafts: Map<number, string>;
  declare _sentSlots: Set<number>;

  constructor() {
    super();
    this.prompt = '';
    this.slots = [];
    this.freeSlots = 0;
    this._drafts = new Map();
    this._sentSlots = new Set();
  }

  static styles = css`
    :host {
      display: block;
      max-width: 36rem;
      margin: 2rem auto;
      padding: 0 1rem;
    }
    h1 {
      margin: 0 0 0.25rem 0;
      letter-spacing: -0.01em;
    }
    .prompt {
      background: var(--bg-card, #1f2128);
      border: 1px solid var(--border, #2c2e36);
      border-radius: 8px;
      padding: 1rem 1.25rem;
      margin: 1rem 0 1.5rem;
      font-size: 1.1rem;
    }
    .row {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      align-items: center;
    }
    input[type='text'] {
      flex: 1;
      min-width: 0;
      padding: 0.5rem 0.6rem;
      background: var(--bg-card, #1f2128);
      color: var(--fg, #ececec);
      border: 1px solid var(--border, #2c2e36);
      border-radius: 6px;
    }
    .count {
      font-variant-numeric: tabular-nums;
      font-size: 0.8rem;
      color: var(--fg-dim, #a0a4ad);
      min-width: 3rem;
      text-align: right;
    }
    .count.over {
      color: var(--accent-warn, #f0a04b);
    }
    .hint {
      color: var(--fg-dim, #a0a4ad);
      font-style: italic;
    }
    .bonus {
      margin-top: 1rem;
    }
  `;

  render() {
    const remaining = this.slots.filter((s) => !this._sentSlots.has(s));
    const allSent = remaining.length === 0;
    return html`
      <link rel="stylesheet" href="/static/style.css" />
      <h1>Write your responses</h1>
      <div class="prompt">${this.prompt}</div>

      ${repeat(remaining, (slotId) => slotId, (slotId) => this.#renderRow(slotId))}

      ${allSent
        ? this.freeSlots > 0
          ? html`
              <div class="bonus">
                <button @click=${() => this.#onClaimBonus()}>
                  Claim a bonus response (${this.freeSlots} free)
                </button>
              </div>
            `
          : html`<p class="hint">Waiting for other players to type&hellip;</p>`
        : null}
    `;
  }

  #renderRow(slotId: number) {
    const draft = this._drafts.get(slotId) ?? '';
    const len = codepointLength(draft);
    const over = len > MAX_LEN;
    return html`
      <div class="row">
        <input
          type="text"
          .value=${draft}
          placeholder="Response #${slotId}"
          @input=${(e: Event) => this.#onInput(slotId, (e.target as HTMLInputElement).value)}
        />
        <span class="count ${over ? 'over' : ''}">${len}/${MAX_LEN}</span>
        <button
          ?disabled=${len === 0 || over}
          @click=${() => this.#onSend(slotId)}
        >
          Send
        </button>
      </div>
    `;
  }

  #onInput(slotId: number, value: string) {
    const next = new Map(this._drafts);
    next.set(slotId, value);
    this._drafts = next;
  }

  #onSend(slotId: number) {
    const text = this._drafts.get(slotId)?.trim() ?? '';
    if (!text) return;
    // Optimistically mark the slot as sent so the row disappears; the server
    // will broadcast vote-start / game-status when everyone is done.
    const sent = new Set(this._sentSlots);
    sent.add(slotId);
    this._sentSlots = sent;
    this.dispatchEvent(
      new CustomEvent('biz-submit-response', {
        detail: { responseId: slotId, response: text },
        bubbles: true,
        composed: true,
      }),
    );
  }

  #onClaimBonus() {
    this.dispatchEvent(
      new CustomEvent('biz-claim-bonus', { bubbles: true, composed: true }),
    );
  }
}

customElements.define('biz-response-entry', BizResponseEntry);
